import { useEffect, useState } from "react";
import { Box } from "@mui/material";

import Gatter from "../DetailGatter/Gatter";
import Line from "../DetailGatter/Line";
import Text from "../DetailGatter/Text";

export default function Tester() {
  // Inputs
  const [inputA, setInputA] = useState(0);
  const [inputB, setInputB] = useState(0);
  const [carryIn, setCarryIn] = useState(0);

  // Interaction state ✅
  const [hasStarted, setHasStarted] = useState(false);

  // Result
  const [testResult, setTestResult] = useState(null);

  // Test values
  const [testXor1, setTestXor1] = useState(0);
  const [testXor2, setTestXor2] = useState(0);
  const [testAnd1, setTestAnd1] = useState(0);
  const [testAnd2, setTestAnd2] = useState(0);
  const [testOr, setTestOr] = useState(0);

  // Logic

  const isReset =
    inputA === 0 &&
    inputB === 0 &&
    carryIn === 0 &&
    testXor1 === 0 &&
    testXor2 === 0 &&
    testAnd1 === 0 &&
    testAnd2 === 0 &&
    testOr === 0;

  // ✅ Evaluation only after user interaction
  useEffect(() => {
    if (isReset) {
      setHasStarted(false);
      setTestResult(null);
      return;
    }

    if (!hasStarted) return;

    const sum1 = inputA ^ inputB;
    const carry1 = inputA & inputB;
    const sumOut = sum1 ^ carryIn;
    const carry2 = sum1 & carryIn;
    const carryOut = carry1 || carry2;

    const checker =
      sum1 === testXor1 &&
      carry1 === testAnd1 &&
      sumOut === testXor2 &&
      carry2 === testAnd2 &&
      carryOut === testOr;

    setTestResult(checker);
  }, [
    isReset,
    hasStarted,
    inputA,
    inputB,
    carryIn,
    testXor1,
    testXor2,
    testAnd1,
    testAnd2,
    testOr,
  ]);

  // ✅ Toggle helper with interaction tracking
  const toggle = (value, setter) => {
    setHasStarted(true);
    setter(value ? 0 : 1);
  };

  return (
    <Box sx={{ width: "95%", maxWidth: 700, mx: "auto" }}>
      {/* ✅ Feedback */}
      <Box
        sx={{
          mt: 2,
          mb: 2,
          p: 2,
          borderRadius: 2,
          textAlign: "center",
          fontWeight: "bold",
          fontSize: "1.1rem",
          color: "#fff",
          backgroundColor: !hasStarted
            ? "#616161"
            : testResult
              ? "#2e7d32"
              : "#c62828",
          boxShadow: testResult
            ? "0 0 10px rgba(46,125,50,0.6)"
            : "0 0 10px rgba(198,40,40,0.6)",
          transition: "all 0.3s ease",
        }}
      >
        {!hasStarted
          ? "⚙️ Beginne deine eigene Testübung"
          : testResult
            ? "✅ Richtig!"
            : "❌ Noch nicht korrekt"}
      </Box>

      {/* ✅ SVG */}
      <svg
        viewBox="0 0 700 300"
        width="100%"
        height="100%"
        style={{ border: "1px solid #ccc" }}
      >
        {/* Animation */}
        <defs>
          <style>
            {`
              .flow {
                stroke-dasharray: 8;
                animation: flow 0.8s linear infinite;
              }
              @keyframes flow {
                from { stroke-dashoffset: 0; }
                to   { stroke-dashoffset: -16; }
              }
            `}
          </style>

          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Inputs */}
        <Text x={20} y={30} text="Ü" color={carryIn} />
        <Line
          x1={60}
          x2={360}
          y={24}
          color={carryIn}
          position="start"
          handleClick={() => toggle(carryIn, setCarryIn)}
        />

        <Text x={20} y={70} text="A" color={inputA} />
        <Line
          x1={60}
          x2={200}
          y={63}
          color={inputA}
          position="start"
          handleClick={() => toggle(inputA, setInputA)}
        />

        <Text x={20} y={100} text="B" color={inputB} />
        <Line
          x1={60}
          x2={200}
          y={92}
          color={inputB}
          position="start"
          handleClick={() => toggle(inputB, setInputB)}
        />

        {/* Gates */}
        <Gatter x={200} y={45} text="XOR" />
        <Gatter x={360} y={10} text="XOR" />
        <Gatter x={200} y={140} text="AND" />
        <Gatter x={360} y={90} text="AND" />
        <Gatter x={500} y={110} text="OR" />

        {/* Connections */}
        <Line
          x1={170}
          x2={200}
          y={63}
          y2={165}
          color={inputA}
          position="angle"
        />
        <Line
          x1={150}
          x2={200}
          y={92}
          y2={185}
          color={inputB}
          position="angle"
        />

        <Line
          x1={340}
          x2={360}
          y={25}
          y2={110}
          color={carryIn}
          position="angle"
        />
        <Line
          x1={320}
          x2={360}
          y={60}
          y2={130}
          color={testXor1}
          position="angle"
        />

        <Line
          x1={300}
          x2={360}
          y={60}
          color={testXor1}
          position="start"
          handleClick={() => toggle(testXor1, setTestXor1)}
        />

        <Line
          x1={460}
          x2={640}
          y={40}
          color={testXor2}
          position="both"
          handleClick={() => toggle(testXor2, setTestXor2)}
        />
        <Text x={665} y={47} text="X" color={testXor2} />

        <Line
          x1={300}
          x2={500}
          y={160}
          color={testAnd1}
          position="start"
          handleClick={() => toggle(testAnd1, setTestAnd1)}
        />

        <Line
          x1={460}
          x2={500}
          y={130}
          color={testAnd2}
          position="start"
          handleClick={() => toggle(testAnd2, setTestAnd2)}
        />

        <Line
          x1={600}
          x2={640}
          y={130}
          color={testOr}
          position="both"
          handleClick={() => toggle(testOr, setTestOr)}
        />
        <Text x={665} y={135} text="Ü" color={testOr} />
      </svg>
    </Box>
  );
}
